import { useCallback, useEffect, useMemo, useState } from 'react';

import { toFileId, toModelId, useGetFile, useGetModel, usePutFile } from '@/hooks/api';
import { MappingItem, useModelConfigStore } from '@/store';

export type UseMappingFormatterMappingOpts = {
  fieldId: string;
  formatterId: string;
};

export const useMappingFormatterMapping = ({ fieldId, formatterId }: UseMappingFormatterMappingOpts) => {
  const modelId = useModelConfigStore((state) => state.modelId);
  const { data: model } = useGetModel(toModelId(modelId));
  const mappingFileUrl = useMemo(() => {
    const f = model?.fieldConfig[fieldId]?.formatters?.find((f) => f.id === formatterId);
    return f?.config?.mappingFileUrl as string | undefined;
  }, [fieldId, formatterId, model?.fieldConfig]);
  const { data: file, isPending, isError } = useGetFile(toFileId(mappingFileUrl), { failureCount: 1 });
  const { mutate: updateMappingFile } = usePutFile();

  const [mapping, setMapping] = useState<MappingItem[] | undefined>();
  const [lastModified, setLastModified] = useState<Date | undefined>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!file) return;

    setMapping(undefined);
    setLastModified(undefined);

    file
      .text()
      .then((str) => {
        setMapping(JSON.parse(str));
        setLastModified(new Date(file.lastModified));
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, [file]);

  const set = useCallback(
    (newMapping: MappingItem[]) => {
      return new Promise((resolve, reject) => {
        if (!mappingFileUrl) {
          reject('Mapping file does not exist');
          return;
        }

        updateMappingFile(
          {
            fileUrl: mappingFileUrl,
            file: new Blob([JSON.stringify(newMapping)], { type: 'application/json' }),
          },
          {
            onSuccess: (file) => resolve(file),
            onError: (error) => reject(error),
          }
        );
      });
    },
    [mappingFileUrl, updateMappingFile]
  );

  return { mapping, setMapping: set, isPending, lastModified, isLoaded, isError: isError || error != null, error };
};
